import React from "react";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import TextField from "@mui/material/TextField";
import LoadingButton from "@mui/lab/LoadingButton";
import CircularProgress from "@mui/material/CircularProgress";
import CloseIcon from "@mui/icons-material/Close";
import StoreOutlinedIcon from "@mui/icons-material/StoreOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { toast } from "react-toastify";

import {
  API_lookup_user_by_email,
  API_add_user_to_company_team,
} from "../../../../constants";
import { postRightworkAPI } from "../../requests/utilities/requests";
import {
  SPECIAL_ACCESS_ICONS,
  SPECIAL_ACCESS_STYLES,
} from "../../requests/manager/employees/EmployeesTableRow";
import { isValidSMSPhone } from "../../../../helpers/helpers";
import { PERMISSION_OPTIONS, STORE_OVERRIDE_OPTIONS } from "./constants";
import DatePickerField from "./DatePickerField";
import { summarizeFailureReasons, logBulkFailures } from "./bulkResults";


const EMAIL_DEBOUNCE_MS = 400;
// Lightweight email format check to avoid hitting the lookup endpoint with
// obvious garbage. The backend re-validates with Django's EmailValidator.
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;


// Email lookup state machine. The banner copy + submit-disabled behavior key
// off this; see render below for the per-state UI.
//   "idle"        — nothing typed yet, or email format invalid
//   "loading"     — request in flight
//   "no_match"    — email doesn't match an existing User (slice 1: blocked)
//   "linkable"    — match, not in this company, can be linked
//   "reactivable" — match but User account is deactivated; grant will reactivate
//   "in_company"  — match, already in this company; submit blocked
//   "error"       — lookup failed (network/server); allow retry
function emailLookupReducer(_state, action) {
  switch (action.type) {
    case "reset":
      return { status: "idle", result: null };
    case "loading":
      return { status: "loading", result: null };
    case "result":
      return { status: action.status, result: action.result };
    case "error":
      return { status: "error", result: null };
    default:
      return { status: "idle", result: null };
  }
}


function LookupBanner({ status, result }) {
  if (status === "idle") return null;
  if (status === "loading") {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500">
        <CircularProgress size={12} sx={{ color: "#94a3b8" }} />
        Looking up email…
      </div>
    );
  }
  if (status === "error") {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
        <WarningAmberIcon style={{ fontSize: 14 }} />
        Lookup failed. Try again or proceed with caution.
      </div>
    );
  }
  if (status === "no_match") {
    return (
      <div className="flex items-start gap-2 rounded-lg border border-violet-200 bg-violet-50 px-3 py-2 text-xs text-violet-800">
        <InfoOutlinedIcon style={{ fontSize: 14, marginTop: 1 }} />
        <span>
          No existing user with this email. We'll create a new Rightwork account
          from the details below.
        </span>
      </div>
    );
  }
  if (status === "in_company") {
    return (
      <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
        <WarningAmberIcon style={{ fontSize: 14, marginTop: 1 }} />
        <span>
          <span className="font-semibold">{result?.full_name}</span> already has access to this company.
        </span>
      </div>
    );
  }
  if (status === "reactivable") {
    return (
      <div className="flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
        <CheckCircleOutlineIcon style={{ fontSize: 14, marginTop: 1 }} />
        <span>
          <span className="font-semibold">{result?.full_name}</span>'s account is currently deactivated.
          Adding them here will reactivate it. Their existing profile (name, contact info) is preserved.
        </span>
      </div>
    );
  }
  // linkable
  return (
    <div className="flex items-start gap-2 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-800">
      <InfoOutlinedIcon style={{ fontSize: 14, marginTop: 1 }} />
      <span>
        <span className="font-semibold">{result?.full_name}</span> is already in another Rightwork company.
        We'll link to their existing account; their profile (name, contact info) is preserved.
      </span>
    </div>
  );
}


export default function AddUserModal({
  open,
  onClose,
  companyId,
  companyStores,
  onSuccess,
}) {
  const [email, setEmail] = React.useState("");
  const [storeIds, setStoreIds] = React.useState([]);
  const [permissionType, setPermissionType] = React.useState("company_employee");
  const [overrides, setOverrides] = React.useState([]);
  const [sendWelcome, setSendWelcome] = React.useState(true);
  const [welcomeChannel, setWelcomeChannel] = React.useState("email");
  // Identity fields — only used when creating a new user (lookup=no_match).
  // Linked users have their identity preserved on the auth.User row, so
  // these are ignored server-side for the link path.
  const [firstName, setFirstName] = React.useState("");
  const [lastName, setLastName] = React.useState("");
  const [phone, setPhone] = React.useState("");
  const [dob, setDob] = React.useState("");
  const [doh, setDoh] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [lookup, dispatchLookup] = React.useReducer(emailLookupReducer, {
    status: "idle",
    result: null,
  });

  // Reset everything when the modal opens. Lookup defaults to idle since the
  // email field is empty — admin types fresh each time.
  React.useEffect(() => {
    if (!open) return;
    setEmail("");
    setStoreIds([]);
    setPermissionType("company_employee");
    setOverrides([]);
    setSendWelcome(true);
    setWelcomeChannel("email");
    setFirstName("");
    setLastName("");
    setPhone("");
    setDob("");
    setDoh("");
    dispatchLookup({ type: "reset" });
  }, [open]);

  // SMS welcome requires a valid supported phone. If admin chose SMS then cleared
  // the phone, snap back to email so submit doesn't fail validation.
  React.useEffect(() => {
    if (welcomeChannel === "sms" && !isValidSMSPhone(phone)) {
      setWelcomeChannel("email");
    }
  }, [phone, welcomeChannel]);

  // Debounced lookup. Token guards against out-of-order responses; bumped
  // synchronously on every email change so an in-flight request whose value
  // is no longer current discards itself when it lands.
  const lookupTokenRef = React.useRef(0);
  React.useEffect(() => {
    // Bump on EVERY effect run (i.e., every email change). This invalidates
    // any in-flight request from a prior email; without this, the admin
    // could clear the field mid-flight and still see a result land.
    lookupTokenRef.current += 1;
    // Reset banner immediately so a stale result from the previous email
    // doesn't linger during the new debounce window.
    dispatchLookup({ type: "reset" });

    const trimmed = email.trim();
    if (!trimmed || !EMAIL_REGEX.test(trimmed) || !companyId) {
      return undefined;
    }
    const handle = setTimeout(async () => {
      const myToken = lookupTokenRef.current;
      dispatchLookup({ type: "loading" });
      try {
        const response = await postRightworkAPI(API_lookup_user_by_email, {
          company_id: companyId,
          email: trimmed,
        });
        if (myToken !== lookupTokenRef.current) return;
        const payload = response?.data?.redux?.payload || {};
        if (!payload.exists) {
          dispatchLookup({ type: "result", status: "no_match", result: null });
        } else if (payload.in_this_company) {
          dispatchLookup({ type: "result", status: "in_company", result: payload });
        } else if (!payload.has_active_user_account) {
          dispatchLookup({ type: "result", status: "reactivable", result: payload });
        } else {
          dispatchLookup({ type: "result", status: "linkable", result: payload });
        }
      } catch (err) {
        if (myToken !== lookupTokenRef.current) return;
        // Surface the underlying error so an outage isn't an opaque
        // banner — the modal still falls through to the lookup-failed
        // branch which lets the admin proceed.
        console.error("Email lookup failed:", err);
        dispatchLookup({ type: "error" });
      }
    }, EMAIL_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [email, companyId]);

  const toggleStore = (id) => {
    setStoreIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };
  const toggleOverride = (key) => {
    setOverrides((prev) =>
      prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key],
    );
  };

  const canLink = lookup.status === "linkable" || lookup.status === "reactivable";
  // Treat the "error" state as a degraded create-path: the lookup endpoint
  // is down but the add endpoint resolves/creates server-side, so admins
  // shouldn't be hard-blocked by a transient lookup outage. We surface the
  // identity fields in this state too (see render below) and apply the same
  // validation as canCreate, since the backend may take either path.
  const lookupFailed = lookup.status === "error";
  const identityComplete =
    firstName.trim().length > 0 &&
    lastName.trim().length > 0 &&
    // SMS welcome needs a valid phone; backend re-checks but failing fast
    // here keeps admins from submitting and getting a 400.
    (!sendWelcome || welcomeChannel !== "sms" || isValidSMSPhone(phone));
  const canCreate = lookup.status === "no_match" && identityComplete;
  const canSubmitOnLookupError = lookupFailed && identityComplete;
  const canSubmit =
    (canLink || canCreate || canSubmitOnLookupError) &&
    storeIds.length > 0 &&
    !submitting &&
    !!permissionType;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const body = {
        company_id: companyId,
        email: email.trim().toLowerCase(),
        store_ids: storeIds.join(","),
        permission_type: permissionType,
        overrides: overrides.join(","),
        send_welcome: sendWelcome,
        welcome_channel: welcomeChannel,
      };
      // Identity fields are only meaningful in the create path; the backend
      // ignores them for the link path but sending them is harmless. Also
      // send them on the lookup-error fallback so the backend has them if
      // it ends up creating.
      if (canCreate || canSubmitOnLookupError) {
        body.first_name = firstName.trim();
        body.last_name = lastName.trim();
        if (phone.trim()) body.phone_number = phone.trim();
        if (dob) body.date_of_birth = dob;
        if (doh) body.date_of_hire = doh;
      }

      const response = await postRightworkAPI(API_add_user_to_company_team, body);
      const payload = response?.data?.redux?.payload || {};
      const succeeded = payload.succeeded_count || 0;
      const failed = payload.failed_count || 0;
      const verb = payload.was_created ? "Created" : "Linked";
      const subject = payload.was_created
        ? `${firstName} ${lastName}`.trim()
        : (lookup.result?.full_name || "user");
      const reactivatedNote = payload.was_reactivated
        ? " Their account was reactivated."
        : "";
      if (failed === 0) {
        toast.success(
          `${verb} ${subject} at ${succeeded} store${succeeded === 1 ? "" : "s"}.${reactivatedNote}`,
          { autoClose: 4000 },
        );
      } else {
        const reasons = summarizeFailureReasons(payload.results);
        logBulkFailures("add_user_to_company_team", payload.results);
        toast.warning(
          `${verb === "Created" ? "Added" : "Linked"} to ${succeeded} of ${succeeded + failed}; ${failed} failed.${reasons ? ` Reasons: ${reasons}` : ""}`,
          { autoClose: 8000 },
        );
      }
      onSuccess?.(payload);
      onClose();
    } catch (error) {
      toast.error(
        error?.response?.data?.system?.message || "Failed to add user.",
        { autoClose: 4000 },
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={submitting ? undefined : onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 560,
          maxHeight: "90vh",
          bgcolor: "white",
          borderRadius: "12px",
          boxShadow: 24,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-900">Add user</h2>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="inline-flex h-7 w-7 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50"
            aria-label="Close"
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>

        <div className="overflow-y-auto px-5 py-4 space-y-5">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500 mb-2">
              User
            </p>
            <label className="block text-[11px] font-medium text-slate-600 mb-0.5">
              Email *
            </label>
            <TextField
              size="small"
              fullWidth
              type="email"
              autoFocus
              placeholder="user@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              InputProps={{
                sx: {
                  fontSize: 13,
                  borderRadius: "6px",
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#6d28d9" },
                },
              }}
            />
            <div
              className="mt-2"
              role="status"
              aria-live="polite"
              aria-atomic="true"
            >
              <LookupBanner status={lookup.status} result={lookup.result} />
            </div>

            {(lookup.status === "no_match" || lookup.status === "error") && (
              <div className="mt-3 space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-[11px] font-medium text-slate-600 mb-0.5">First name *</label>
                    <TextField
                      size="small"
                      fullWidth
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      InputProps={{
                        sx: {
                          fontSize: 13,
                          borderRadius: "6px",
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#6d28d9" },
                        },
                      }}
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-medium text-slate-600 mb-0.5">Last name *</label>
                    <TextField
                      size="small"
                      fullWidth
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      InputProps={{
                        sx: {
                          fontSize: 13,
                          borderRadius: "6px",
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#6d28d9" },
                        },
                      }}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-slate-600 mb-0.5">Phone</label>
                  <TextField
                    size="small"
                    fullWidth
                    type="tel"
                    placeholder="(555) 555-5555 or +52..."
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    InputProps={{
                      sx: {
                        fontSize: 13,
                        borderRadius: "6px",
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#6d28d9" },
                      },
                    }}
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-[11px] font-medium text-slate-600 mb-0.5">Date of birth</label>
                    <DatePickerField value={dob} onChange={setDob} />
                  </div>
                  <div>
                    <label className="block text-[11px] font-medium text-slate-600 mb-0.5">Date of hire</label>
                    <DatePickerField value={doh} onChange={setDoh} />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500 mb-2">
              Initial access
            </p>
            <label className="block text-[11px] font-medium text-slate-600 mb-1">
              Stores *
            </label>
            <div className="max-h-[180px] overflow-y-auto rounded-lg border border-slate-200">
              {companyStores.length === 0 && (
                <div className="px-3 py-6 text-center text-sm text-slate-500">No stores in this company.</div>
              )}
              {companyStores.map((s) => {
                const checked = storeIds.includes(s.id);
                const isClosed = s.status === "closed";
                return (
                  <label
                    key={s.id}
                    className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-slate-50"
                  >
                    <Checkbox
                      checked={checked}
                      onChange={() => toggleStore(s.id)}
                      size="small"
                      sx={{ color: "#cbd5e1", "&.Mui-checked": { color: "#6d28d9" }, py: 0.5 }}
                    />
                    <StoreOutlinedIcon style={{ fontSize: 16 }} className="text-slate-400" />
                    <span className={`text-sm ${checked ? "text-violet-800 font-medium" : "text-slate-700"}`}>
                      {s.store_name}
                    </span>
                    {isClosed && (
                      <span className="ml-auto px-1.5 py-0.5 text-[10px] uppercase tracking-wider rounded bg-slate-100 text-slate-500">
                        closed
                      </span>
                    )}
                  </label>
                );
              })}
            </div>

            <div className="mt-3">
              <label className="block text-[11px] font-medium text-slate-600 mb-1">
                Permission *
              </label>
              <Select
                value={permissionType}
                onChange={(e) => setPermissionType(e.target.value)}
                size="small"
                fullWidth
                sx={{ borderRadius: "8px", "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#6d28d9" } }}
              >
                {PERMISSION_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </div>

            <div className="mt-3">
              <label className="block text-[11px] font-medium text-slate-600 mb-1">
                Special access (optional)
              </label>
              <div className="flex flex-wrap gap-1.5">
                {STORE_OVERRIDE_OPTIONS.map((opt) => {
                  const checked = overrides.includes(opt.value);
                  const Icon = SPECIAL_ACCESS_ICONS[opt.value];
                  const styles = SPECIAL_ACCESS_STYLES[opt.value];
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => toggleOverride(opt.value)}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-xs transition-colors ${
                        checked
                          ? "border-violet-600 bg-violet-50 text-violet-700"
                          : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                      }`}
                    >
                      {Icon && styles && (
                        <span
                          className={`inline-flex h-4 w-4 items-center justify-center rounded border ${styles.border} ${styles.bg}`}
                        >
                          <Icon className={styles.text} style={{ fontSize: 10 }} />
                        </span>
                      )}
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mt-1">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={sendWelcome}
                    onChange={(e) => setSendWelcome(e.target.checked)}
                    sx={{ color: "#cbd5e1", "&.Mui-checked": { color: "#6d28d9" } }}
                  />
                }
                label={<span className="text-sm text-slate-700">Send a welcome message</span>}
              />
              {/* Channel radios only shown when creating a new user — linked
                  users are already onboarded, can't accept invites via SMS,
                  so we force email server-side. Echoing that here keeps the
                  UI honest. Lookup-error fallback also shows the radios
                  since the backend may take the create path. */}
              {sendWelcome && (lookup.status === "no_match" || lookup.status === "error") && (
                <div className="ml-9 -mt-1 flex items-center gap-4">
                  <label className="flex items-center cursor-pointer text-sm text-slate-700">
                    <input
                      type="radio"
                      name="add-user-welcome-channel"
                      value="email"
                      checked={welcomeChannel === "email"}
                      onChange={() => setWelcomeChannel("email")}
                      className="mr-1.5 accent-violet-700"
                    />
                    Email
                  </label>
                  <label
                    className={`flex items-center cursor-pointer text-sm ${
                      isValidSMSPhone(phone) ? "text-slate-700" : "cursor-not-allowed text-slate-400"
                    }`}
                  >
                    <input
                      type="radio"
                      name="add-user-welcome-channel"
                      value="sms"
                      checked={welcomeChannel === "sms"}
                      onChange={() => setWelcomeChannel("sms")}
                      disabled={!isValidSMSPhone(phone)}
                      className="mr-1.5 accent-violet-700"
                    />
                    SMS
                    {!isValidSMSPhone(phone) && (
                      <span className="ml-1 text-[11px]">
                        {phone ? "(invalid phone)" : "(requires phone)"}
                      </span>
                    )}
                  </label>
                </div>
              )}
              {sendWelcome && lookup.status !== "no_match" && lookup.status !== "error" && (
                <p className="ml-9 -mt-1 text-[11px] text-slate-500">
                  One email per user (not per store).
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-200 bg-slate-50">
          {/* Surface the lookup-in-flight state so admins don't wonder why
              the submit button is disabled mid-debounce. */}
          {lookup.status === "loading" && (
            <span className="mr-auto text-[11px] text-slate-500">
              Checking email…
            </span>
          )}
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 rounded-lg text-sm text-slate-700 hover:bg-white disabled:opacity-50"
          >
            Cancel
          </button>
          <LoadingButton
            variant="contained"
            loading={submitting}
            disabled={!canSubmit}
            onClick={handleSubmit}
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              backgroundColor: "#6d28d9",
              "&:hover": { backgroundColor: "#5b21b6" },
              fontSize: 14,
              px: 2.5,
              py: 1,
            }}
          >
            {canCreate ? "Create & add" : "Add user"}
          </LoadingButton>
        </div>
      </Box>
    </Modal>
  );
}
